/**
 * Cross-realm `instanceof TouchEvent` shim for native navigation.
 *
 * Under iOS native navigation the whole React tree runs in the main webview but
 * touch events are dispatched from the plugin's child webviews (a different JS
 * realm). `e instanceof TouchEvent` compares against the *main* window's TouchEvent
 * constructor, and cross-realm `instanceof` is always false. Libraries that branch on
 * `e instanceof TouchEvent` (notably react-simple-pull-to-refresh, used by
 * StyledPullToRefresh) take the wrong path: they never read the touch's coordinates,
 * their scroll-direction check degenerates, and they call `preventDefault()` on every
 * touchmove, killing scrolling on every PTR screen.
 *
 * Replacing TouchEvent's `Symbol.hasInstance` with a structural check makes the test
 * realm-agnostic: any real touch event (one carrying `touches` / `changedTouches`)
 * passes, regardless of which webview created it. This is installed on iOS native nav
 * only; web and Android never call it and keep the platform-native behaviour.
 */
let installed = false;

export function installCrossRealmTouchEventShim(): void {
  if (installed || typeof TouchEvent === "undefined") return;
  installed = true;

  Object.defineProperty(TouchEvent, Symbol.hasInstance, {
    configurable: true,
    value(obj: unknown): boolean {
      return obj != null && typeof obj === "object" && "touches" in obj && "changedTouches" in obj;
    },
  });
}
